import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper } from '@mui/material';
import { has } from 'lodash';
import { darken, lighten } from '@mui/material/styles';

import { ContentEmailSpecListInput, UserSegmentFragment } from 'api';
import { EmptyState } from 'components';
import { AdminContentEmailCreate } from 'Links';
import theme from 'theme';

import { Header, Table, TableHeader } from 'features/admin/components/ContentEmails';
import { useContentEmailHistory, useContentEmailSpecs } from 'features/admin/hooks';
import { CONTENT_EMAIL_LIMIT } from 'features/admin/constants';
import SentTable from 'features/admin/components/ContentEmails/SentTable';

interface Props {
  isLoading?: boolean;
  segments: UserSegmentFragment[];
  userCount: number;
}

const styles = {
  emptyStateAction: {
    backgroundColor: lighten(theme.palette.primary.light, 0.3),
    color: 'primary.dark',
    padding: theme.spacing(1, 4),
    marginTop: 2,

    '&:hover': {
      backgroundColor: 'primary.light',
      color: darken(theme.palette.primary.dark, 0.1),
    },
  },
} as const;

const ContentEmails: FC<Props> = ({ segments, userCount }) => {
  const { t } = useTranslation();

  const [filter, setFilter] = useState<ContentEmailSpecListInput>({
    limit: CONTENT_EMAIL_LIMIT,
    recurringOnly: true,
  });
  const [isSent, setIsSent] = useState(false);

  const contentEmailQueryOptions = useContentEmailSpecs({ data: filter });
  const contentEmailHistoryQueryOptions = useContentEmailHistory({
    data: { limit: CONTENT_EMAIL_LIMIT },
  });

  const { isEmpty } = contentEmailQueryOptions;

  function renderEmptyState() {
    function getName() {
      if (!has(filter, 'recurringOnly')) {
        return 'contentEmailsSent';
      }

      return filter.recurringOnly ? 'contentEmailsEnabled' : 'contentEmailsDisabled';
    }

    return (
      <EmptyState
        name={getName()}
        actionComponent={
          <Button
            sx={styles.emptyStateAction}
            size="large"
            variant="contained"
            color="primary"
            component={AdminContentEmailCreate}
          >
            {t('contentEmails:emptyStateAction')}
          </Button>
        }
      />
    );
  }

  function renderTable() {
    if (isSent) {
      if (contentEmailHistoryQueryOptions.isEmpty) {
        return renderEmptyState();
      }

      return <SentTable segments={segments} {...contentEmailHistoryQueryOptions} />;
    }

    if (isEmpty) {
      return renderEmptyState();
    }

    return (
      <Table
        numActiveUsers={userCount}
        segments={segments}
        filter={filter}
        {...contentEmailQueryOptions}
      />
    );
  }

  return (
    <Fragment>
      <Box pb={2}>
        <Header />
      </Box>

      <Paper>
        <TableHeader filter={filter} setFilter={setFilter} setIsSent={setIsSent} />
        {renderTable()}
      </Paper>
    </Fragment>
  );
};

export default ContentEmails;
